import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';

// Fonction globale pour télécharger des fichiers depuis Supabase
if (typeof window !== 'undefined') {
  window.downloadSupabaseFile = async (url, fileName) => {
    try {
      // Récupérer le fichier
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Créer un lien de téléchargement
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      
      // Simuler un clic pour déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Erreur lors du téléchargement du fichier :', error);
    }
  };
}

// Fonction utilitaire pour parser la réponse du bot
function parseBotAnswer(answer) {
  if (!answer) return '';
  let html = answer;

  // Mise en gras du texte entre ** et **
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Transformation des liens Supabase en boutons de téléchargement
  html = html.replace(/(https?:\/\/[\w\-./?#=&;%+~]+supabase[\w\-./?#=&;%+~]+)(?![^<]*>|[^<>]*<\/(a|code|pre)>)/gi, function(match) {
    // Extraction du nom du fichier à partir de l'URL
    const fileName = match.split('/').pop() || 'document';
    return `<button onclick="window.downloadSupabaseFile('${match}', '${fileName}')" class="bg-secondary text-white px-3 py-1 rounded-md hover:bg-secondary-dark transition duration-300 inline-flex items-center justify-center my-2"><svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>Télécharger</button>`;
  });

  // Liens : transforme les autres URLs en liens cliquables avec une approche plus robuste
  const processedParts = [];
  let lastIndex = 0;
  
  // Expression régulière améliorée pour détecter les URLs
  const urlRegex = /(https?:\/\/|www\.)[a-z0-9-]+(\.[a-z0-9-]+)+([/?#].*)?/gi;
  let match;
  
  // Créer une copie temporaire du HTML pour travailler dessus
  let tempHtml = html;
  
  // Trouver toutes les correspondances avec leur position
  while ((match = urlRegex.exec(tempHtml)) !== null) {
    // Vérifier si l'URL est déjà dans une balise HTML
    const beforeMatch = tempHtml.substring(0, match.index);
    const openTags = (beforeMatch.match(/<[^>]*$/g) || []).length;
    const closeTags = (beforeMatch.match(/>[^<]*$/g) || []).length;
    
    // Si l'URL est déjà dans une balise, la sauter
    if (openTags > closeTags || 
        /<(a|code|pre|button)[^>]*>[^<]*$/.test(beforeMatch) || 
        tempHtml.substring(match.index).match(/^[^<]*<\/(a|code|pre|button)>/)) {
      continue;
    }
    
    // Ajouter le texte avant l'URL
    if (match.index > lastIndex) {
      processedParts.push(tempHtml.substring(lastIndex, match.index));
    }
    
    // Récupérer l'URL complète
    const url = match[0];
    
    // Vérifier si c'est un lien Supabase (déjà traité précédemment)
    if (url.includes('supabase')) {
      processedParts.push(url); // Garder tel quel car déjà traité
    } else {
      // Ajouter l'URL sous forme de lien
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      processedParts.push(`<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${url}</a>`);
    }
    
    // Mettre à jour l'index pour la prochaine itération
    lastIndex = match.index + url.length;
  }
  
  // Ajouter le reste du texte après la dernière URL
  if (lastIndex < tempHtml.length) {
    processedParts.push(tempHtml.substring(lastIndex));
  }
  
  // Reconstruire le HTML avec les liens transformés
  html = processedParts.join('');

  // Listes numérotées (1. ... 2. ...)
  // On détecte les blocs commençant par "1." suivis de "2." etc. et on les transforme en <ol><li>...</li>...</ol>
  html = html.replace(/(\d+\. .+?)(?=(\n\d+\.|$))/gs, function(match) {
    // Découpe chaque item
    const items = match.split(/\n(?=\d+\.)/g);
    if (items.length === 1) return match; // Pas une vraie liste
    return '<ol class="list-decimal pl-5">' + items.map(item => '<li>' + item.replace(/^\d+\. /, '') + '</li>').join('') + '</ol>';
  }); // Pas de \ à enlever ici, regex correcte

  // Listes à puces ("- ", "• ")
  html = html.replace(/((?:[-•] .+\n?)+)/g, function(match) {
    const items = match.trim().split(/\n/).filter(Boolean);
    if (items.length === 1) return match;
    return '<ul class="list-disc pl-5">' + items.map(item => '<li>' + item.replace(/^[-•] /, '') + '</li>').join('') + '</ul>';
  }); // Pas de \ à enlever ici, regex correcte

  // Paragraphes & sauts de ligne
  html = html.replace(/\n{2,}/g, '</p><p>'); // Double saut = paragraphe
  html = html.replace(/\n/g, '<br/>'); // Simple saut = <br>
  html = '<p>' + html + '</p>';

  return html;
}

const Chatbot = ({ onClose }) => {
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      text: "Bonjour ! Je suis votre assistant virtuel pour les services consulaires. Comment puis-je vous aider aujourd'hui ?", 
      sender: 'bot' 
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Référence à l'historique de conversation pour l'API
  const [conversationHistory, setConversationHistory] = useState([{
    role: 'assistant',
    content: "Bonjour ! Je suis votre assistant virtuel pour les services consulaires. Comment puis-je vous aider aujourd'hui ?"
  }]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fonction pour détecter la langue du message
  const detectLanguage = (text) => {
    // Détection simple basée sur les caractères et mots courants
    const frenchPatterns = /[éèêëàâäôöùûüÿçÉÈÊËÀÂÄÔÖÙÛÜŸÇ]|\b(je|tu|il|elle|nous|vous|ils|elles|bonjour|merci|s'il vous plaît|comment|pourquoi|quand)\b/i;
    const englishPatterns = /\b(the|a|an|i|you|he|she|we|they|hello|thank|please|how|why|when)\b/i;
    
    // Vérifier les patterns français d'abord
    if (frenchPatterns.test(text)) {
      return 'fr';
    } 
    // Ensuite vérifier les patterns anglais
    else if (englishPatterns.test(text)) {
      return 'en';
    }
    // Par défaut, on suppose que c'est du français (langue principale du site)
    return 'fr';
  };

  // Fonction pour détecter l'intention du message
  const detectIntent = (text) => {
    const lowerText = text.toLowerCase();
    
    // Intentions liées aux documents
    if (/passeport|carte d'identité|cni|pièce d'identité|document/.test(lowerText)) {
      return 'documents_identité';
    }
    
    // Intentions liées aux visas
    if (/visa|entrée|séjour|immigration/.test(lowerText)) {
      return 'visa';
    }
    
    // Intentions liées aux coûts
    if (/coût|prix|tarif|combien|payer|frais/.test(lowerText)) {
      return 'tarifs';
    }
    
    // Intentions liées aux délais
    if (/délai|temps|durée|attente|quand|combien de temps/.test(lowerText)) {
      return 'délais';
    }
    
    // Intentions liées aux procédures
    if (/procédure|démarche|comment|étape|processus/.test(lowerText)) {
      return 'procédures';
    }
    
    // Intentions liées aux rendez-vous
    if (/rendez-vous|rdv|prendre rendez-vous|réserver|disponibilité/.test(lowerText)) {
      return 'rendez_vous';
    }
    
    // Par défaut
    return 'information_générale';
  };

  // Fonction pour détecter le sujet du chat
  const detectChatTopic = (text) => {
    const lowerText = text.toLowerCase();
    
    // Sujets principaux
    if (/passeport/.test(lowerText)) {
      return 'passeport';
    }
    
    if (/carte d'identité|cni/.test(lowerText)) {
      return 'carte_identité';
    }
    
    if (/visa|entrée|séjour/.test(lowerText)) {
      return 'visa';
    }
    
    if (/légalisation|document légal|authentification/.test(lowerText)) {
      return 'légalisation';
    }
    
    if (/certificat|acte de naissance|état civil/.test(lowerText)) {
      return 'état_civil';
    }
    
    if (/mariage|divorce/.test(lowerText)) {
      return 'mariage_divorce';
    }
    
    if (/décès|décédé/.test(lowerText)) {
      return 'décès';
    }
    
    if (/rendez-vous|rdv/.test(lowerText)) {
      return 'rendez_vous';
    }
    
    if (/paiement|payer|frais/.test(lowerText)) {
      return 'paiement';
    }
    
    // Par défaut
    return 'information_générale';
  };

  // Fonction pour enregistrer le message dans la base de données
  const storeUserMessage = async (messageText) => {
    try {
      // Détection des informations
      const language = detectLanguage(messageText);
      const intent = detectIntent(messageText);
      const chatTopic = detectChatTopic(messageText);
      
      // Utilisation du service API pour stocker le message
      await api.storeChatMessage({
        message: messageText,
        language,
        intent,
        chat_topic: chatTopic
      });
      
      console.log('Message utilisateur enregistré avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du message:', error);
      // On ne bloque pas l'expérience utilisateur en cas d'erreur
    }
  };

  const handleSendMessage = async (e, customText = null) => {
    if (e && e.preventDefault) e.preventDefault();
    const messageToSend = customText !== null ? customText : input;
    if (!messageToSend.trim()) return;

    // Add user message to chat
    const userMessage = {
      id: messages.length + 1,
      text: messageToSend,
      sender: 'user'
    };
    setMessages(prev => [...prev, userMessage]);

    // Mettre à jour l'historique de conversation avec le message utilisateur
    const updatedHistory = [...conversationHistory, { role: 'user', content: messageToSend }];
    setConversationHistory(updatedHistory);

    // Enregistrer le message utilisateur dans la base de données
    storeUserMessage(messageToSend);

    setInput('');
    setIsLoading(true);

    try {
      // Utiliser le service API pour envoyer le message au chatbot avec le contexte
      const response = await api.sendChatMessageWithContext(messageToSend, updatedHistory);
    
      // Extraction des nouvelles clés answer et knowledge
      const { answer, knowledge } = response.data;
    
      // Ajout du message bot (on stocke aussi le HTML parsé)
      const botMessage = {
        id: messages.length + 2,
        text: answer,
        sender: 'bot',
        parsedHtml: parseBotAnswer(answer),
        knowledge: knowledge // Stockage de la source de connaissance si fournie
      };
      setMessages(prev => [...prev, botMessage]);
    
      // Mettre à jour l'historique avec la réponse du bot
      const assistantResponse = { role: 'assistant', content: answer };
      
      // Si knowledge est disponible, on remplace le dernier message de l'utilisateur par knowledge
      if (knowledge) {
        // On prend tous les messages sauf le dernier (qui est le message utilisateur)
        const historyWithoutLastUserMessage = conversationHistory.slice(0, -1);
        // On ajoute un nouveau message utilisateur avec knowledge comme contenu
        const updatedUserMessage = { role: 'user', content: knowledge };
        // On met à jour l'historique avec le message utilisateur modifié et la réponse du bot
        setConversationHistory([...historyWithoutLastUserMessage, updatedUserMessage, assistantResponse]);
      } else {
        // Comportement normal si pas de knowledge
        setConversationHistory([...conversationHistory, assistantResponse]);
      }
    } catch (error) {
      console.error('Error sending message to chatbot:', error);
      // Add error message
      const errorMessage = {
        id: messages.length + 2,
        text: "Désolé, je rencontre des difficultés à traiter votre demande. Veuillez réessayer plus tard ou contactez-nous directement.",
        sender: 'bot',
        parsedHtml: null
      };
      setMessages(prev => [...prev, errorMessage]);
    
      // Ajouter également le message d'erreur à l'historique
      setConversationHistory(prev => [...prev, { 
        role: 'assistant', 
        content: "Désolé, je rencontre des difficultés à traiter votre demande. Veuillez réessayer plus tard ou contactez-nous directement."
      }]);
    } finally {
      setIsLoading(false);
    }
    // Remet le focus sur le champ de saisie
    inputRef.current?.focus();
  };

  // Common questions suggestions
  const commonQuestions = [
    "Quels sont les documents à fournir pour établir un passeport ?",
    "Combien coûte une légalisation ?",
    "Quels documents pour la carte consulaire et combien ça coûte ?",
  ];

  const handleQuickQuestion = (question) => {
    handleSendMessage(null, question);
  };


  return (
    <div className="chatbot-container">
      <div className="chatbot-header">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-md">
            <img 
              src="/assets/img/armoiries.png" 
              alt="Armoiries de la Côte d'Ivoire" 
              className="w-8 h-8 object-contain"
            />
          </div>
          <h3 className="text-lg font-semibold">Assistant Consulaire</h3>
        </div>
        <button onClick={onClose} className="text-white !bg-transparent">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      
      <div className="chatbot-messages">
        {messages.map(message => (
          <div 
            key={message.id} 
            className={message.sender === 'user' ? 'user-message' : 'bot-message'}
          >
            {message.sender === 'bot' && message.parsedHtml ? (
              <>
                <span dangerouslySetInnerHTML={{ __html: message.parsedHtml }} />
                {/* Affichage du contexte source supprimé pour ne pas surcharger la conversation */}
              </>
            ) : (
              message.text
            )}
          </div>
        ))} 
        
        {isLoading && (
          <div className="bot-message flex items-center space-x-2">
            <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Quick questions */}
      {messages.find(m => m.sender === 'user') === undefined && (
        <div className="px-4 py-3 bg-gray-50 flex flex-col space-y-3">
          {commonQuestions.map((question, index) => (
            <button
              key={index}
              onClick={() => handleQuickQuestion(question)}
              className="text-xs bg-white rounded-full px-4 py-2 hover:bg-gray-100 transition-all border-0 shadow-none w-full mb-2"
            >
              {question}
            </button>
          ))}
        </div>
      )}
      
      <form onSubmit={handleSendMessage} className="chatbot-input">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Posez votre question..."
          className="flex-grow px-3 py-2 border-none focus:outline-none focus:ring-0"
          disabled={isLoading}
        />
        <button
          type="submit"
          className="bg-secondary text-white p-2 rounded-full disabled:opacity-50 !border-0 !shadow-none aspect-square flex items-center justify-center"
          disabled={isLoading || !input.trim()}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </form>
    </div>
  );
};

Chatbot.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default Chatbot;
